import L from "leaflet";

// Base layers configuration
export const BASE_LAYERS = [
  {
    name: "CartoDB Positron",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: "© CartoDB",
  },
  {
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "© OpenStreetMap contributors",
  },
  {
    name: "Satellite (Esri)",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
  },
  /* {
    name: "CartoDB Dark",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: "© CartoDB",
  }, */
];

// Status colors mapping
export const STATUS_COLORS = {
  active: "bg-green-100 text-green-800 border-green-200",
  inactive: "bg-gray-100 text-gray-800 border-gray-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  "in-progress": "bg-blue-100 text-blue-800 border-blue-200",
  planned: "bg-purple-100 text-purple-800 border-purple-200",
  delayed: "bg-orange-100 text-orange-800 border-orange-200",
};

// Project status colors (consistent with ProjectMap)
const PROJECT_STATUS_COLORS = {
  completed: "#22c55e",
  "in-progress": "#3b82f6",
  pending: "#f59e0b",
  cancelled: "#ef4444",
  active: "#22c55e",
  inactive: "#6b7280",
  planned: "#a855f7",
  delayed: "#f97316",
  default: "#6b7280",
};

// Helper function to create project icon HTML
const createProjectIconHTML = (color) => `
  <div style="
    width: 30px;
    height: 30px;
    background-color: ${color};
    border: 3px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 12px rgba(0,0,0,0.3);
    position: relative;
  ">
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 12px;
      height: 12px;
      background-color: #ffffff;
      border-radius: 50%;
    "></div>
  </div>
`;

// Project icon configurations (Circle markers untuk membedakan dari excavation)
const PROJECT_ICONS = Object.fromEntries(
  Object.entries(PROJECT_STATUS_COLORS).map(([status, color]) => [
    status,
    new L.DivIcon({
      className: "custom-project-marker",
      html: createProjectIconHTML(color),
      iconSize: [26, 26],
      iconAnchor: [13, 13],
      popupAnchor: [0, -13],
    }),
  ])
);
// Excavation status colors (small circles untuk membedakan dari project)
const EXCAVATION_STATUS_COLORS = {
  active: "#22c55e", // green
  planned: "#eab308", // yellow
  completed: "#3b82f6", // blue
  suspended: "#ef4444", // red
  inactive: "#9ca3af", // gray
  "in-progress": "#3b82f6", // blue
  default: "#9ca3af", // gray
};

// Helper function to create excavation icon HTML (small circle)
const createExcavationIconHTML = (color) => `
  <div style="
    width: 14px;
    height: 14px;
    background-color: ${color};
    border: 2px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    position: relative;
  "></div>
`;

// Excavation icon configurations (Small circles untuk membedakan dari project)
const EXCAVATION_ICONS = Object.fromEntries(
  Object.entries(EXCAVATION_STATUS_COLORS).map(([status, color]) => [
    status,
    new L.DivIcon({
      className: "custom-excavation-marker",
      html: createExcavationIconHTML(color),
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      popupAnchor: [0, -7],
    }),
  ])
);

/**
 * Get status color classes
 * @param {string} status - The status to get colors for
 * @returns {string} CSS classes for status styling
 */
export const getStatusColor = (status) => {
  return STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS.inactive;
};

/**
 * Create project icon based on status
 * @param {string} status - Project status
 * @returns {L.Icon} Leaflet icon
 */
export const getProjectIcon = (status) => {
  const normalizedStatus = status?.toLowerCase();
  return PROJECT_ICONS[normalizedStatus] || PROJECT_ICONS.default;
};

/**
 * Create excavation icon based on status
 * @param {string} status - Excavation status
 * @returns {L.Icon} Leaflet icon
 */
export const getExcavationIcon = (status) => {
  const normalizedStatus = status?.toLowerCase();
  return EXCAVATION_ICONS[normalizedStatus] || EXCAVATION_ICONS.default;
};

/**
 * Normalize coordinate object to [lat, lng]
 * Supports both GeoJSON { coordinates: [lng, lat] }
 * and { lat, lng }
 */
export const getLatLng = (coord) => {
  if (!coord) return null;

  // GeoJSON
  if (Array.isArray(coord.coordinates)) {
    return [coord.coordinates[1], coord.coordinates[0]];
  }

  // LatLng object
  if (coord.lat !== undefined && coord.lng !== undefined) {
    return [coord.lat, coord.lng];
  }

  return null;
};

/**
 * Convert decimal degrees to DMS (Degrees, Minutes, Seconds)
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {string} Formatted DMS string
 */
export const convertToDMS = (lat, lng) => {
  const formatDMS = (coord, type) => {
    const absolute = Math.abs(coord);
    const degrees = Math.floor(absolute);
    const minutesFloat = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesFloat);
    const seconds = Math.round((minutesFloat - minutes) * 60 * 100) / 100;

    const direction =
      type === "lat" ? (coord >= 0 ? "N" : "S") : coord >= 0 ? "E" : "W";

    return `${degrees}°${minutes}'${seconds.toFixed(1)}"${direction}`;
  };

  return `${formatDMS(lat, "lat")}, ${formatDMS(lng, "lng")}`;
};

/**
 * Calculate statistics from projects and excavations data
 * @param {Array} projects - Projects array
 * @param {Array} excavations - Excavations array
 * @returns {Object} Statistics object
 */
export const calculateStats = (projects, excavations) => {
  const projectStats = projects.reduce((acc, project) => {
    const status = project.status?.toLowerCase() || "inactive";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const excavationStats = excavations.reduce((acc, excavation) => {
    const status = excavation.status?.toLowerCase() || "inactive";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const totalReports = excavations.reduce((sum, exc) => {
    return sum + (exc.reports?.length || 0);
  }, 0);

  return {
    projects: {
      total: projects.length,
      byStatus: projectStats,
      active: projectStats.active || 0,
      inactive: projectStats.inactive || 0,
      pending: projectStats.pending || 0,
      completed: projectStats.completed || 0,
      cancelled: projectStats.cancelled || 0,
      "in-progress": projectStats["in-progress"] || 0,
      planned: projectStats.planned || 0,
      delayed: projectStats.delayed || 0,
    },
    excavations: {
      total: excavations.length,
      byStatus: excavationStats,
      active: excavationStats.active || 0,
      inactive: excavationStats.inactive || 0,
      pending: excavationStats.pending || 0,
      completed: excavationStats.completed || 0,
      cancelled: excavationStats.cancelled || 0,
      "in-progress": excavationStats["in-progress"] || 0,
    },
    reports: {
      total: totalReports,
    },
  };
};

/**
 * Generate connections between projects and excavations
 * @param {Array} projects - Filtered projects
 * @param {Array} excavations - Filtered excavations
 * @param {boolean} showConnections - Whether to show connections
 * @returns {Array} Array of connection objects
 */
export const generateConnections = (projects, excavations, showConnections) => {
  if (!showConnections) return [];

  const connections = [];
  let connectionId = 0;

  excavations.forEach((excavation) => {
    if (!excavation.Project || !excavation.coordinates) return;

    const relatedProject = projects.find((p) => p.id === excavation.Project.id);
    if (!relatedProject || !relatedProject.coordinates) return;

    const projectPos = getLatLng(relatedProject.coordinates);
    const excavationPos = getLatLng(excavation.coordinates);

    if (projectPos && excavationPos) {
      connections.push({
        id: `connection-${connectionId++}`,
        positions: [projectPos, excavationPos],
        projectId: relatedProject.id,
        excavationId: excavation.id,
      });
    }
  });

  return connections;
};

/**
 * Calculate optimal map center from projects and excavations
 * @param {Array} projects - Projects array
 * @param {Array} excavations - Excavations array
 * @returns {Array} [lat, lng] center coordinates
 */
export const calculateMapCenter = (projects, excavations) => {
  const validCoordinates = [];

  // Collect project coordinates
  projects.forEach((project) => {
    if (
      project.coordinates &&
      project.coordinates.lat &&
      project.coordinates.lng
    ) {
      validCoordinates.push({
        lat: project.coordinates.lat,
        lng: project.coordinates.lng,
      });
    }
  });

  // Collect excavation coordinates
  excavations.forEach((excavation) => {
    if (excavation.coordinates) {
      validCoordinates.push({
        lat: excavation.coordinates.lat,
        lng: excavation.coordinates.lng,
      });
    }
  });

  // If no valid coordinates, return default center (Jakarta)
  if (validCoordinates.length === 0) {
    return [-6.2088, 106.8456];
  }

  // Calculate center
  const avgLat =
    validCoordinates.reduce((sum, coord) => sum + coord.lat, 0) /
    validCoordinates.length;
  const avgLng =
    validCoordinates.reduce((sum, coord) => sum + coord.lng, 0) /
    validCoordinates.length;

  return [avgLat, avgLng];
};

/**
 * Get unique status options from data
 * @param {Array} data - Array of projects or excavations
 * @param {string} field - Field to extract status from (default: 'status')
 * @returns {Array} Array of unique status values
 */
export const getUniqueStatuses = (data, field = "status") => {
  const statuses = data
    .map((item) => item[field])
    .filter((status) => status && status.trim() !== "")
    .map((status) => status.toLowerCase());

  return [...new Set(statuses)].sort();
};

/**
 * Filter data based on search term and status
 * @param {Array} data - Data to filter
 * @param {string} searchTerm - Search term
 * @param {string} statusFilter - Status filter
 * @param {Array} searchFields - Fields to search in
 * @returns {Array} Filtered data
 */
export const filterData = (
  data,
  searchTerm,
  statusFilter,
  searchFields = ["name"]
) => {
  return data.filter((item) => {
    // Status filter
    const matchesStatus =
      statusFilter === "all" ||
      item.status?.toLowerCase() === statusFilter.toLowerCase();

    // Search filter
    const matchesSearch =
      searchTerm === "" ||
      searchFields.some((field) => {
        const value = getNestedValue(item, field);
        return value && value.toLowerCase().includes(searchTerm.toLowerCase());
      });

    return matchesStatus && matchesSearch;
  });
};

/**
 * Get nested object value by dot notation
 * @param {Object} obj - Object to search in
 * @param {string} path - Dot notation path (e.g., 'Project.name')
 * @returns {*} Found value or undefined
 */
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

/**
 * Debounce function for search input
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Format number with thousand separators
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  return new Intl.NumberFormat("id-ID").format(num);
};

/**
 * Get status display name
 * @param {string} status - Status code
 * @returns {string} Display name
 */
export const getStatusDisplayName = (status) => {
  const statusNames = {
    active: "Aktif",
    inactive: "Tidak Aktif",
    pending: "Menunggu",
    completed: "Selesai",
    cancelled: "Dibatalkan",
    "in-progress": "Sedang Berlangsung",
    planned: "Direncanakan",
    delayed: "Tertunda",
  };

  return statusNames[status?.toLowerCase()] || status || "Tidak Diketahui";
};
